import {
    AssessmentForm,
    EvaluationFormResponse,
    SurveyForm,
    VisibilityCondition,
} from "@genesys/cloud-frontend/models";
import { BaseQuestionType } from "features/qualityForm/common/BaseQuestionType";
import { VisibilityConditionPredicate } from "features/qualityForm/qualityFormSlices/index";

type AnswerLocation = {
    questionGroup: number;
    question: number;
    answer: number | "PROMOTER" | "PASSIVE" | "DETRACTOR";
};

function getLocationToAnswer(
    regexPath: string,
    questionGroupIndex: number
): AnswerLocation {
    const answerRegex = /answer\/(PROMOTER|PASSIVE|DETRACTOR|[0-9]+)/;
    const questionRegex = /question\/([0-9]+)/;
    const questionGroupRegex = /questionGroup\/([0-9]+)/;
    const answer = regexPath.match(answerRegex)?.[1];
    return {
        questionGroup: Number(
            regexPath.match(questionGroupRegex)?.[1] ?? questionGroupIndex
        ),
        question: Number(regexPath.match(questionRegex)?.[1]),
        answer:
            answer !== "PROMOTER" &&
            answer !== "PASSIVE" &&
            answer !== "DETRACTOR"
                ? Number(answer)
                : answer,
    };
}

export function getIdsFromLocation<
    T extends EvaluationFormResponse | SurveyForm | AssessmentForm
>(locationToAnswer: AnswerLocation, form?: T): VisibilityConditionPredicate {
    const questionGroups = form?.questionGroups;
    let questionGroupId;
    let questionId;
    let answerId;
    const isAnswerNumber = typeof locationToAnswer.answer === "number";
    if (questionGroups) {
        const questionGroup = questionGroups[locationToAnswer.questionGroup];
        const question = questionGroup?.questions?.[locationToAnswer.question];
        questionGroupId = questionGroup?.id;
        questionId = question?.id;
        const questionType = question?.type;
        let answerOption;
        // TODO MULTIPLESELECT: Convert back bracket notation to dot notation when cloud frontend lib is ready
        if (
            questionType &&
            questionType === BaseQuestionType.MultipleSelectQuestion
        ) {
            answerOption =
                question?.multipleSelectOptionQuestions?.[
                    locationToAnswer.answer
                ];
        } else {
            answerOption = question?.answerOptions?.[locationToAnswer.answer];
        }
        answerId = isAnswerNumber ? answerOption?.id : locationToAnswer.answer;
        if (!questionGroupId || !questionId || !answerId) {
            throw new Error(" unable to get form fields");
        }
        return {
            questionGroupId,
            questionId,
            answerId,
        };
    } else {
        // when answer is not number, return the original answer value
        if (!isAnswerNumber) {
            answerId = locationToAnswer.answer;
        }
        return {
            questionGroupId,
            questionId,
            answerId,
        };
    }
}

function convertVisibilityConditionPredicates<
    T extends EvaluationFormResponse | SurveyForm | AssessmentForm
>(
    visibilityCondition: VisibilityCondition,
    questionGroupIndex: number,
    form: T
) {
    if (visibilityCondition.predicates) {
        visibilityCondition.predicates = visibilityCondition.predicates.map(
            (regexPathToAnswer) => {
                return getIdsFromLocation<T>(
                    getLocationToAnswer(
                        regexPathToAnswer as any as string,
                        questionGroupIndex
                    ),
                    form
                );
            }
        );
    }
}

export function addAnswerInfoToVisibilityPredicates<
    T extends EvaluationFormResponse | SurveyForm | AssessmentForm
>(form: T) {
    form.questionGroups?.forEach((questionGroup, index) => {
        //within questions
        questionGroup.questions?.forEach((question) => {
            const visibilityCondition = question.visibilityCondition;
            if (visibilityCondition) {
                convertVisibilityConditionPredicates<T>(
                    visibilityCondition,
                    index,
                    form
                );
            }
        });
        //within question groups
        if (questionGroup.visibilityCondition) {
            convertVisibilityConditionPredicates<T>(
                questionGroup.visibilityCondition,
                index,
                form
            );
        }
    });
}
